import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { AlertTriangle, CheckCircle2, TrendingDown, TrendingUp, Activity } from 'lucide-react';
import type { DriftData, Drift } from '@/hooks/useDviDrift';

interface DviDriftAlertsListProps {
  driftData: DriftData | null | undefined;
  isLoading: boolean;
}

const driftIcons: Record<string, React.ElementType> = {
  overcall_rising: TrendingUp,
  approval_dropping: TrendingDown,
  confidence_inflation: Activity,
  override_spike: AlertTriangle,
};

const severityColors: Record<string, string> = {
  low: 'bg-yellow-100 text-yellow-800 border-yellow-200',
  medium: 'bg-orange-100 text-orange-800 border-orange-200',
  high: 'bg-red-100 text-red-800 border-red-200',
};

const severityBadgeVariant: Record<string, 'outline' | 'secondary' | 'destructive'> = {
  low: 'outline',
  medium: 'secondary',
  high: 'destructive',
};

export function DviDriftAlertsList({ driftData, isLoading }: DviDriftAlertsListProps) {
  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Pattern Alerts</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <Skeleton className="h-20 w-full" />
          <Skeleton className="h-20 w-full" />
        </CardContent>
      </Card>
    );
  }

  const drifts = driftData?.drifts ?? [];

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <AlertTriangle className="h-5 w-5" />
          Pattern Alerts
        </CardTitle>
        <CardDescription>
          Anomalies detected in DVI patterns that may need attention
        </CardDescription>
      </CardHeader>
      <CardContent>
        {drifts.length === 0 ? (
          <div className="flex items-center gap-3 p-4 bg-green-50 rounded-lg border border-green-200">
            <CheckCircle2 className="h-5 w-5 text-green-600" />
            <div>
              <p className="font-medium text-green-800">No pattern anomalies detected</p>
              <p className="text-sm text-green-700">
                Your DVI patterns are within normal ranges
              </p>
            </div>
          </div>
        ) : (
          <div className="space-y-3">
            {drifts.map((drift, index) => (
              <DriftAlertItem key={index} drift={drift} />
            ))}
          </div>
        )}
        
        {driftData?.last_checked && (
          <p className="text-xs text-muted-foreground mt-4">
            Last checked: {new Date(driftData.last_checked).toLocaleString()}
          </p>
        )}
      </CardContent>
    </Card>
  );
}

function DriftAlertItem({ drift }: { drift: Drift }) {
  const Icon = driftIcons[drift.drift_type] || AlertTriangle;

  return (
    <Alert className={severityColors[drift.severity]}>
      <Icon className="h-4 w-4" />
      <div className="flex-1">
        <AlertTitle className="flex items-center gap-2">
          Pattern Drift Detected
          <Badge variant={severityBadgeVariant[drift.severity]} className="text-xs">
            {drift.severity}
          </Badge>
        </AlertTitle>
        <AlertDescription className="mt-1">
          <p>{drift.message}</p>
          <div className="flex flex-wrap gap-2 mt-2 text-xs">
            <span className="bg-background/50 px-2 py-1 rounded">
              Change: {drift.change_percent > 0 ? '+' : ''}{drift.change_percent}%
            </span>
            {drift.affected_systems.length > 0 && (
              <span className="bg-background/50 px-2 py-1 rounded">
                Systems: {drift.affected_systems.join(', ')}
              </span>
            )}
          </div>
        </AlertDescription>
      </div>
    </Alert>
  );
}
